'use client';

import React from 'react';
import { styled } from '@mui/material/styles';

import { TvlTreemapWidget } from 'page-components/widgets';
import { getProtocolRatingsPageData } from 'api/selectors/pages';
import { useServerData } from 'utils/react';
import {
  GlobalPreloader,
  Loading,
  BgContainer,
  SectionWithTitle,
  OverallRatingWidget,
} from 'components';
import { useRouteUtils } from 'routes';
import { ProtocolAvgRatingTranscription } from 'components/transcriptions';
import { ProductSlug, VersionSlug, NetworkSlug } from 'domain/types';

import { BadMarketsTable } from './BadMarketsTable';
import { takeTopBadMarkets } from './helpers';

export type ProtocolRatingsProps = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  data: string;
};

export function ProtocolRatings({ data, productSlug, versionSlug, network }: ProtocolRatingsProps) {
  const pageDataRD = useServerData(
    () => getProtocolRatingsPageData({ productSlug, versionSlug, network }),
    [network, productSlug, versionSlug],
    data,
  );
  const { redirectTo } = useRouteUtils();

  return (
    <Loading data={pageDataRD} loader={<GlobalPreloader />}>
      {({ marketsData, ratingInfo }) => {
        const topBadMarketsData = takeTopBadMarkets(marketsData);

        return (
          <Root>
            <OverallRatingItem>
              <OverallRatingWidget
                rating={ratingInfo.map(r => r.rating.rating)}
                description={ratingInfo.fold(
                  // eslint-disable-next-line react/no-unstable-nested-components
                  info => (
                    <ProtocolAvgRatingTranscription {...info} />
                  ),
                  () => null,
                )}
              />
            </OverallRatingItem>
            <SectionWithTitle
              title="Markets"
              detailsLinkProps={redirectTo.marketsRatings({ productSlug, versionSlug, network })}
            >
              {topBadMarketsData.length > 0 && <BadMarketsTable tableData={topBadMarketsData} />}
              <MarketsChart>
                <TvlTreemapWidget
                  title="Markets · by TVL and Health"
                  productSlug={productSlug}
                  network={network}
                  data={{
                    type: 'markets',
                    info: marketsData,
                  }}
                />
              </MarketsChart>
            </SectionWithTitle>
          </Root>
        );
      }}
    </Loading>
  );
}

const Root = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 60,
}));

const OverallRatingItem = styled('div')(() => ({
  minHeight: 336,
  height: 1,
}));

const MarketsChart = styled(BgContainer)(() => ({
  minHeight: 636,
  height: 1,
}));
